import type { SyncResult } from '@bnn/shared';
import { useSyncExternalStore } from 'react';
import { api, ApiError } from '../lib/api';
import { getDeviceId } from '../lib/kv';
import { IdbOutboxStore } from './idbStore';
import { OfflineError, Outbox, type OutboxSender } from './outbox';

export type { EntryOp, OutboxOp, VoidOp } from './outbox';

const wrapNetwork = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (e) {
    // No network, gateway errors, or the server failing: keep the ops and retry later.
    if (e instanceof ApiError && (e.code === 'NETWORK' || e.status >= 500)) throw new OfflineError(e.message);
    throw e;
  }
};

const sender: OutboxSender = {
  syncEntries: (entries) =>
    wrapNetwork(async () => (await api.post<{ results: SyncResult[] }>('/sync/entries', { device_id: await getDeviceId(), entries })).results),
  syncVoids: (operations) =>
    wrapNetwork(async () => (await api.post<{ results: SyncResult[] }>('/sync/operations', { device_id: await getDeviceId(), operations })).results),
};

// M3: persisted in IndexedDB — pending entries survive reloads, killed tabs and browser restarts.
export const outbox = new Outbox(new IdbOutboxStore(), sender);
void outbox.init();

let version = 0;
outbox.subscribe(() => version++);

/** Re-render when the outbox changes. */
export function useOutbox() {
  useSyncExternalStore(
    (cb) => outbox.subscribe(cb),
    () => version,
  );
  return outbox;
}
